import React, { FormEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import * as api from "services/Api";
import "./admin-console.scss";
import { useOfficeTranslation } from "i18n/OfficeTranslation";
import { OfficeTranslationKey } from "i18n/officeTranslations";
import { publishAdminConsoleVisibility } from "./adminConsoleVisibility";

type ConsoleResource = "project" | "account" | "invoice" | "person";

type ParsedCommand =
    | { kind: "clear" }
    | { kind: "close" }
    | { kind: "help" }
    | { kind: "list"; resource: ConsoleResource }
    | { kind: "get" | "delete"; resource: ConsoleResource; id: string }
    | { kind: "error"; key: OfficeTranslationKey; params?: Record<string, string | number> };

type ConsoleLine = {
    id: number;
    kind: "command" | "error" | "info" | "success";
    text: string;
};

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const helpLineKeys: OfficeTranslationKey[] = [
    "common.adminConsole.help.heading",
    "common.adminConsole.help.list",
    "common.adminConsole.help.get",
    "common.adminConsole.help.delete",
    "common.adminConsole.help.clear",
    "common.adminConsole.help.exit",
];

const resourceAliases: Record<string, ConsoleResource | undefined> = {
    project: "project",
    projects: "project",
    account: "account",
    accounts: "account",
    invoice: "invoice",
    invoices: "invoice",
    person: "person",
    persons: "person",
};

export const parseAdminCommand = (rawValue: string): ParsedCommand => {
    const value = rawValue.trim();
    if (!value) return { kind: "error", key: "common.adminConsole.errors.commandRequired" };

    const parts = value.split(/\s+/);
    const command = parts[0].toLowerCase();
    if (command === "help" && parts.length === 1) return { kind: "help" };
    if (command === "clear" && parts.length === 1) return { kind: "clear" };
    if (["exit", "close"].includes(command) && parts.length === 1) return { kind: "close" };

    if (!["list", "get", "delete"].includes(command)) {
        return { kind: "error", key: "common.adminConsole.errors.unknownCommand" };
    }

    const resource = resourceAliases[parts[1]?.toLowerCase()];
    if (!resource) {
        return {
            kind: "error",
            key: "common.adminConsole.errors.unknownResource",
            params: {resource: parts[1] ?? ""},
        };
    }
    if (command === "list") {
        return parts.length === 2
            ? { kind: "list", resource }
            : { kind: "error", key: "common.adminConsole.errors.listUsage", params: {resource} };
    }
    if (parts.length !== 3) {
        return { kind: "error", key: "common.adminConsole.errors.commandUsage", params: {command, resource} };
    }
    if (!uuidPattern.test(parts[2])) {
        return { kind: "error", key: "common.adminConsole.errors.invalidUuid" };
    }

    return { kind: command as "get" | "delete", resource, id: parts[2].toLowerCase() };
};

const listResource: Record<ConsoleResource, () => Promise<unknown[]>> = {
    project: api.getProjects,
    account: api.getAccounts,
    invoice: api.getInvoices,
    person: api.getPersons,
};

const getResource: Record<ConsoleResource, (id: string) => Promise<unknown>> = {
    project: api.getProject,
    account: api.getAccount,
    invoice: api.getInvoice,
    person: api.getPerson,
};

const deleteResource: Record<ConsoleResource, (id: string) => Promise<void>> = {
    project: api.deleteProject,
    account: api.deleteAccount,
    invoice: api.deleteInvoiceById,
    person: api.deletePersonById,
};

const resourceLabelKey: Record<ConsoleResource, OfficeTranslationKey> = {
    project: "common.project",
    account: "common.account",
    invoice: "common.invoice",
    person: "common.person",
};

const pluralResourceLabelKey: Record<ConsoleResource, OfficeTranslationKey> = {
    project: "common.projects",
    account: "common.accounts",
    invoice: "common.invoices",
    person: "common.persons",
};

const entitySummary = (resource: ConsoleResource, value: unknown, t: ReturnType<typeof useOfficeTranslation>["t"]) => {
    const entity = value as Record<string, unknown>;
    const id = String(entity.id ?? t("common.adminConsole.noId"));
    if (resource === "account") return `${id}  ${String(entity.legal_name ?? t("common.adminConsole.noName"))}`;
    if (resource === "invoice") {
        const state = t(entity.draft ? "common.status.draft" : "common.status.final");
        return `${id}  ${String(entity.name ?? t("common.adminConsole.noName"))}  [${state}]`;
    }
    if (resource === "person") {
        const name = [entity.name, entity.last_name].filter(Boolean).join(" ");
        return `${id}  ${name || t("common.adminConsole.noName")}`;
    }
    return `${id}  ${String(entity.name ?? t("common.adminConsole.noName"))}`;
};

const formatEntity = (value: unknown) => JSON.stringify(value, null, 2) ?? String(value);

const AdminConsole: React.FC = () => {
    const { t } = useOfficeTranslation();
    const nextLineId = useRef(1);
    const inputRef = useRef<HTMLInputElement>(null);
    const outputRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [value, setValue] = useState("");
    const [history, setHistory] = useState<string[]>([]);
    const [historyIndex, setHistoryIndex] = useState(0);
    const [lines, setLines] = useState<ConsoleLine[]>(() => [
        { id: 0, kind: "info", text: t("common.adminConsole.welcome") },
    ]);

    const appendLines = (...newLines: Omit<ConsoleLine, "id">[]) => {
        setLines(current => [
            ...current,
            ...newLines.map(line => ({ ...line, id: nextLineId.current++ })),
        ]);
    };

    useEffect(() => {
        const toggle = (event: globalThis.KeyboardEvent) => {
            if ((event.key === "F13" || event.code === "F13") && !event.repeat) {
                event.preventDefault();
                setOpen(current => !current);
            }
        };
        window.addEventListener("keydown", toggle);
        return () => window.removeEventListener("keydown", toggle);
    }, []);

    useEffect(() => {
        publishAdminConsoleVisibility(open);
    }, [open]);

    useEffect(() => () => publishAdminConsoleVisibility(false), []);

    useEffect(() => {
        if (open) window.setTimeout(() => inputRef.current?.focus(), 0);
    }, [open]);

    useEffect(() => {
        if (open && outputRef.current) outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }, [lines, open]);

    const run = async (event: FormEvent) => {
        event.preventDefault();
        if (busy) return;

        const enteredValue = value.trim();
        const parsed = parseAdminCommand(enteredValue);
        if (enteredValue) {
            setHistory(current => [...current, enteredValue]);
            setHistoryIndex(history.length + 1);
        }
        setValue("");

        if (parsed.kind === "clear") {
            setLines([]);
            return;
        }
        if (parsed.kind === "close") {
            setOpen(false);
            return;
        }

        if (enteredValue) appendLines({ kind: "command", text: enteredValue });
        if (parsed.kind === "error") {
            appendLines({ kind: "error", text: t(parsed.key, parsed.params) });
            return;
        }
        if (parsed.kind === "help") {
            appendLines(...helpLineKeys.map(key => ({ kind: "info" as const, text: t(key) })));
            return;
        }

        setBusy(true);
        try {
            if (parsed.kind === "list") {
                const entities = await listResource[parsed.resource]();
                appendLines({
                    kind: "info",
                    text: entities.length === 0
                        ? t("common.adminConsole.noResourcesFound", {resources: t(pluralResourceLabelKey[parsed.resource])})
                        : t("common.adminConsole.resourceList", {
                            resources: t(pluralResourceLabelKey[parsed.resource]),
                            count: entities.length,
                            entries: entities.map(entity => entitySummary(parsed.resource, entity, t)).join("\n"),
                        }),
                });
            } else if (parsed.kind === "get") {
                const entity = await getResource[parsed.resource](parsed.id);
                appendLines({ kind: "info", text: formatEntity(entity) });
            } else {
                await deleteResource[parsed.resource](parsed.id);
                appendLines({
                    kind: "success",
                    text: t(parsed.resource === "project"
                        ? "common.adminConsole.projectDeleted"
                        : "common.adminConsole.resourceDeleted", {
                        resource: t(resourceLabelKey[parsed.resource]),
                        id: parsed.id,
                    }),
                });
                window.dispatchEvent(new CustomEvent("office:resource-deleted", {
                    detail: { resource: parsed.resource, id: parsed.id },
                }));
            }
        } catch (reason: any) {
            appendLines({
                kind: "error",
                text: t(parsed.kind === "list"
                    ? "common.adminConsole.resourceLoadFailed"
                    : parsed.kind === "get"
                        ? "common.adminConsole.resourceGetFailed"
                        : "common.adminConsole.resourceDeleteFailed", {
                    resource: t(parsed.kind === "list"
                        ? pluralResourceLabelKey[parsed.resource]
                        : resourceLabelKey[parsed.resource]),
                    error: reason?.response?.data?.error ?? reason?.message ?? t("common.adminConsole.unknownError"),
                }),
            });
        } finally {
            setBusy(false);
        }
    };

    const browseHistory = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Escape") {
            setOpen(false);
            return;
        }
        if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;

        event.preventDefault();
        if (event.key === "ArrowUp") {
            const nextIndex = Math.max(0, historyIndex - 1);
            setHistoryIndex(nextIndex);
            setValue(history[nextIndex] ?? "");
        } else {
            const nextIndex = Math.min(history.length, historyIndex + 1);
            setHistoryIndex(nextIndex);
            setValue(nextIndex === history.length ? "" : history[nextIndex]);
        }
    };

    if (!open) return null;

    return (
        <section className="admin-console" role="dialog" aria-label={t("common.adminConsole.officeConsole")}>
            <header className="admin-console__header">
                <span><i className="mdi mdi-console-line me-2" />{t("common.adminConsole.officeConsole")}</span>
                <button
                    aria-label={t("common.adminConsole.closeConsole")}
                    className="admin-console__close"
                    onClick={() => setOpen(false)}
                    type="button"
                >
                    <i className="mdi mdi-close" />
                </button>
            </header>
            <div className="admin-console__output" ref={outputRef} aria-live="polite">
                {lines.map(line => (
                    <div className={`admin-console__line admin-console__line--${line.kind}`} key={line.id}>
                        {line.kind === "command" && <span aria-hidden="true">$ </span>}
                        {line.text}
                    </div>
                ))}
                {busy && <div className="admin-console__line admin-console__line--info">{t("common.adminConsole.working")}</div>}
            </div>
            <form className="admin-console__prompt" onSubmit={run}>
                <label htmlFor="admin-console-command" aria-hidden="true">$</label>
                <input
                    aria-label={t("common.adminConsole.consoleCommand")}
                    autoComplete="off"
                    disabled={busy}
                    id="admin-console-command"
                    onChange={event => setValue(event.target.value)}
                    onKeyDown={browseHistory}
                    ref={inputRef}
                    spellCheck={false}
                    value={value}
                />
            </form>
        </section>
    );
};

export default AdminConsole;
